/**
 * Apply Discount Code Action with Basket API Integration
 * Triggered by: arc:action:applyDiscountCode
 */

import { syncWithBasketApi } from '@src/utils/basket-api/basketApiUtils';

export const runApplyDiscountCode = async (data = {}) => {
    try {
        const {
            discountCode,
            skipBasketApi = false
        } = data;

        if (!discountCode) {
            throw new Error('Discount code is required');
        }

        // If basket API is enabled, try it first
        let basketApiResult = null;
        if (!skipBasketApi && window.basketApiConfig?.enabled) {
            try {
                basketApiResult = await syncWithBasketApi('discount_submit', {
                    discountCode,
                    customerId: window.customerData?.id
                });

                if (basketApiResult.success) {
                    const discountApplications = basketApiResult.processedResult.discountApplications;
                    const successfulApplication = discountApplications.find(app => app.code === discountCode && app.success);

                    if (successfulApplication) {
                        // Dispatch success event
                        window.dispatchEvent(new CustomEvent('discount:applied', {
                            detail: {
                                code: discountCode,
                                success: true,
                                amount: successfulApplication.amount,
                                message: successfulApplication.message || 'Discount applied successfully',
                                basketApiResult
                            }
                        }));

                        return {
                            success: true,
                            code: discountCode,
                            amount: successfulApplication.amount,
                            message: 'Discount applied successfully',
                            basketApiResult
                        };
                    } else {
                        // Find the failed application
                        const failedApplication = discountApplications.find(app => app.code === discountCode && !app.success);
                        const errorMessage = failedApplication?.message || 'Discount code could not be applied';
                        throw new Error(errorMessage);
                    }
                }
            } catch (basketApiError) {
                console.warn('Basket API discount application failed:', basketApiError);
                // Fall back to standard Shopify discount application
            }
        }

        // Fallback to standard Shopify discount application
        // Note: Standard Shopify doesn't have a direct API for discount codes
        // This would typically be handled by the cart form or checkout
        console.warn('Falling back to standard discount application - this may require form submission');

        // Dispatch event for components to handle the fallback
        window.dispatchEvent(new CustomEvent('discount:fallback_required', {
            detail: {
                code: discountCode,
                reason: 'Basket API not available or failed'
            }
        }));

        return {
            success: false,
            error: 'Discount application requires basket API integration',
            fallbackRequired: true
        };

    } catch (error) {
        console.error('Error applying discount code:', error);

        // Dispatch error event
        window.dispatchEvent(new CustomEvent('discount:apply_error', {
            detail: {
                error: error.message,
                code: data.discountCode
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

export default runApplyDiscountCode;
